import { useState, useEffect } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Typography,
  IconButton,
} from "@mui/material";
import { Family } from "protogen/advisors_service_pb";
import { useNavigate } from "react-router-dom";
import useIsMobile from "components/hooks/useIsMobile";
import { useRunWorkflow } from "services/workflow";
import { RunWorkflowRequest } from "protogen/workflow_service_pb";
import AILoading from "components/common/AILoading";
import { useWebSocketContext } from "components/context/SocketConsumerProvider";
import { useSnackpack } from "components/context/SnackpackContextProvider";
import FlexBox from "components/common/FlexBox";
import { Heart } from "lucide-react";
import { ReactComponent as SparklesIcon } from "../../icons/Advisor/Sparkles.svg";
import LinkRouter from "components/navigation/LinkRouter";
import { useCreateMemberTaskWishlistEntry } from "services/tasks";
import { useMemberDetails } from "components/context/MemberDetailsContext";
import { taskDiscoveryAddTaskClause } from "common/memberModuleResolver";

const InspiredTask = ({
  title,
  style = "contained",
  onTaskClick,
  onWishlistClick,
  showWishlistButton = true,
}: {
  title: string;
  style?: "contained" | "normal";
  onTaskClick: (title: string) => void;
  onWishlistClick?: (title: string) => void;
  showWishlistButton?: boolean;
}) => {
  const isMobile = useIsMobile();
  const [isFilled, setIsFilled] = useState(false);

  return (
    <FlexBox
      direction={isMobile ? "column" : "row"}
      variant={style == "contained" ? "contained" : "default"}
      justifyContent="space-between"
      alignItems="center"
      gap="8px"
      sx={{ paddingRight: isMobile ? "" : "16px" }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography variant="body" color="#3D3D3D">
          {title}
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "12px",
          alignItems: "center",
        }}
      >
        <Button
          size="small"
          variant="outlined"
          onClick={() => onTaskClick(title)}
          sx={{
            height: "32px",
          }}
        >
          Add
        </Button>
        {showWishlistButton && onWishlistClick && (
          <IconButton
            size="small"
            disabled={isFilled}
            onClick={() => {
              onWishlistClick(title);
              setIsFilled(true);
            }}
            sx={{}}
          >
            <Heart
              size={24}
              {...(isFilled
                ? {
                    fill: "#EF7B77",
                    stroke: "#EF7B77",
                  }
                : {
                    stroke: "#8E9598",
                  })}
            />
          </IconButton>
        )}
      </Box>
    </FlexBox>
  );
};

type Task = {
  title: string;
  details: string;
};

const CATEGORIES = [
  { title: "Top picks", value: "top_picks" },
  { title: "Seasonal", value: "seasonal" },
  { title: "Home", value: "home" },
  { title: "Wellness", value: "wellness" },
];

export default ({
  family,
  showWishlistButton = true,
  addBackground = false,
  forceLoading = false,
  integrationCount,
}: {
  family: Family;
  showWishlistButton?: boolean;
  addBackground?: boolean;
  forceLoading: boolean;
  integrationCount: number;
}) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { familyStatus, isDigitalSubscription, isUnpaidSignup } =
    useMemberDetails();
  const planFlags = {
    familyStatus,
    isDigitalSubscription,
    isUnpaidSignup,
  };
  const { pushSnack } = useSnackpack();
  const { registerHandler, unregisterHandler } = useWebSocketContext();
  const [category, setCategory] = useState<string>("top_picks");
  const [allTasks, setAllTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);
  const { request: workflowRequest } = useRunWorkflow();
  const { request: createWishlistEntry } = useCreateMemberTaskWishlistEntry();
  const isLoading = forceLoading || loading;
  useEffect(() => {
    const handler = (channel: string, tasksData: any) => {
      setLoading(false);
      // Maybe the sync call handled this first.
      setAllTasks((tasks) =>
        tasks.length > 0 ? tasks : (tasksData as Task[]),
      );
    };

    registerHandler("task-suggestions/", handler);
    return () => {
      unregisterHandler("task-suggestions/", handler);
    };
  }, []);

  useEffect(() => {
    // Generate personalized tasks using family-context-prompted
    generatePersonalizedTasks();
  }, [family, category]);

  const generatePersonalizedTasks = async () => {
    if (!family?.ref || isLoading) return;
    setLoading(true);
    setAllTasks([]);
    const request = new RunWorkflowRequest({
      workflowName: "family-task-suggestions",
      arguments: [
        // Used as cache buster.
        {
          name: "integration_count",
          value: { case: "intValue", value: integrationCount + 1 },
        },
        {
          name: "category",
          value: {
            case: "stringValue",
            value: category,
          },
        },
      ],
      entityType: 0,
      entityRef: family.ref,
      runAsync: false,
    });
    const result = await workflowRequest(request);

    if (result) {
      const data = JSON.parse(result.result);
      // Maybe the socket handled this first.
      setAllTasks((tasks) => (tasks.length > 0 ? tasks : data.tasks));
    }
    setLoading(false);
  };

  const onTaskClick = (title: string) => {
    navigate("/tasks/add", {
      state: { taskTitle: title, family: family },
    });
  };

  const onWishlistClick = async (title: string) => {
    const resp = await createWishlistEntry({ title });
    if (resp) {
      pushSnack({
        message: "Task saved!",
        key: resp.taskWishlistEntry?.ref || "",
        action: {
          label: "View wishlist",
          href: "/tasks#wishlist",
        },
      });
    }
  };

  // Use all tasks since they're filtered server-side by category
  const filteredTasks = allTasks;

  return (
    <FlexBox
      direction="column"
      gap="20px"
      sx={{
        ...(addBackground && {
          background: "white",
          padding: "16px 20px",
          borderRadius: "12px",
          border: "1px solid #D4D4D4",
        }),
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "8px",
          alignItems: "start",
          justifyContent: "space-between",
        }}
      >
        <FlexBox alignItems="center" sx={{ width: "100%" }}>
          <FlexBox gap="8px" alignItems="center">
            <SparklesIcon height="20px" width="20px" />
            <Typography
              variant="h4"
              color="#3D3D3D"
              sx={{
                display: "flex",
                lineHeight: 1,
              }}
            >
              Task ideas for you
            </Typography>
          </FlexBox>
        </FlexBox>
        <Typography variant="body" color="text.secondary">
          {taskDiscoveryAddTaskClause(planFlags)}, or use the{" "}
          <IconButton
            size="small"
            onClick={() => navigate("/tasks#wishlist")}
            sx={{ padding: "0px" }}
          >
            <Heart size={16} />
          </IconButton>{" "}
          to save it for later. Browse more ideas in{" "}
          <LinkRouter to="/task-discovery" inline sx={{ color: "#198282" }}>
            Task Discovery
          </LinkRouter>
          .
        </Typography>
      </Box>
      <Box
        sx={{
          width: isMobile ? "100vw !important" : "100%",
          ...(isMobile && {
            marginLeft: "-24px",
            marginRight: "-24px",
            paddingLeft: "24px",
            paddingRight: "24px",
            overflowX: "auto",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          }),
        }}
      >
        <ButtonGroup
          sx={{
            display: "flex",
            flexWrap: isMobile ? "nowrap" : "wrap",
            width: isMobile ? "max-content" : "100%",
            minWidth: isMobile ? "100%" : "auto",
            ...(isMobile && {
              gap: "8px",
            }),
          }}
        >
          {CATEGORIES.map((cat) => (
            <Button
              key={cat.value}
              sx={{
                flexShrink: 0,
                whiteSpace: "nowrap",
                minWidth: "auto",
                paddingX: "16px",
                ...(isMobile && {
                  flex: "0 0 auto",
                }),
                ...(!isMobile && {
                  flex: "1 1 auto",
                }),
              }}
              variant={
                category === cat.value ? "buttonGroupSelected" : "buttonGroup"
              }
              onClick={() => setCategory(cat.value)}
            >
              {cat.title}
            </Button>
          ))}
        </ButtonGroup>
      </Box>
      <FlexBox direction="column" gap="8px" sx={{ minHeight: "300px" }}>
        {isLoading ? (
          <FlexBox direction="column" justifyContent="center">
            <AILoading textAnimated={true} />
          </FlexBox>
        ) : (
          filteredTasks.map((task, i) => (
            <InspiredTask
              key={i}
              title={task.title}
              onTaskClick={onTaskClick}
              onWishlistClick={onWishlistClick}
              showWishlistButton={showWishlistButton}
            />
          ))
        )}
      </FlexBox>
    </FlexBox>
  );
};
